/**
 * Two-Corner Layout (Phase 6).
 *
 * Rules:
 *  1. Exactly two corners are filled — the two endpoints of a single long
 *     edge of the canvas. The other two corners are intentional whitespace.
 *  2. Title and Metadata each take one of those two filled corners.
 *  3. Determinism: same (seed, aspect) → same TextLayout; randomness via
 *     rngFromSeed only.
 *
 * Long edges per aspect:
 *   - landscape (16x9):  top (tl,tr)  AND bottom (bl,br)
 *   - portrait  (9x16, 4x5): left (tl,bl) AND right (tr,br)
 */
import type { AspectId, Corner, TextLayout } from '../types.js';
import { rngFromSeed } from '../utils/seed.js';

/** Returns the long-edge corner pairs available for the aspect. */
export function cornersForAspect(
  aspect: AspectId,
): { longEdgePairs: [Corner, Corner][] } {
  if (aspect === '16x9') {
    return { longEdgePairs: [['tl', 'tr'], ['bl', 'br']] };
  }
  return { longEdgePairs: [['tl', 'bl'], ['tr', 'br']] };
}

function pickFrom<T>(rng: () => number, arr: readonly T[]): T {
  const idx = Math.floor(rng() * arr.length);
  const safeIdx = idx >= arr.length ? arr.length - 1 : idx;
  const value = arr[safeIdx];
  if (value === undefined) throw new Error('pickFrom: empty array');
  return value;
}

export function pickCorners(seed: string, aspect: AspectId): TextLayout {
  const rng = rngFromSeed(seed);
  const { longEdgePairs } = cornersForAspect(aspect);

  // 1. Pick which long edge is filled.
  const edge = pickFrom(rng, longEdgePairs);

  // 2. Within the edge, decide which side gets title vs. metadata.
  const titleSide = rng() < 0.5 ? 0 : 1;
  const titleCorner = edge[titleSide];
  const metaCorner = edge[1 - titleSide];
  if (!titleCorner || !metaCorner) {
    throw new Error('pickCorners: edge missing both corners');
  }

  return {
    title: { corner: titleCorner },
    metadata: { corner: metaCorner },
  };
}
